import { defaultState } from './defaults';
import { STATE_VERSION, type AppState } from './types';

const KEY = 'iron-ledger/state/v1';

/**
 * Everything lives in localStorage on this device. That is the whole point —
 * no account, no server, works with the gym's dead wifi. The trade-off is that
 * clearing site data wipes it, so Settings → Backup writes a JSON file you can
 * keep. Import restores it anywhere.
 */
export function loadState(): AppState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as Partial<AppState>;
    return migrate(parsed);
  } catch (err) {
    console.error('Could not read saved state, starting fresh', err);
    return defaultState();
  }
}

export function saveState(state: AppState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (err) {
    // Private mode, or the quota is full. Losing a write is better than
    // crashing mid-set, so just surface it in the console.
    console.error('Could not save state', err);
  }
}

/**
 * Fills in anything a newer version of the app added, so an old backup or an
 * old browser copy still loads instead of blowing up on a missing field.
 */
function migrate(saved: Partial<AppState>): AppState {
  const base = defaultState();
  return {
    ...base,
    ...saved,
    version: STATE_VERSION,
    profile: { ...base.profile, ...saved.profile },
    settings: {
      ...base.settings,
      ...saved.settings,
      // Nested objects need merging explicitly — a spread would replace the
      // whole thing with whatever an older version happened to save.
      health: { ...base.settings.health, ...saved.settings?.health },
    },
    exercisePrefs: { ...base.exercisePrefs, ...saved.exercisePrefs },
    workouts: saved.workouts ?? [],
    cardio: saved.cardio ?? [],
    metrics: saved.metrics ?? [],
    activeWorkout: saved.activeWorkout ?? null,
    healthDays: saved.healthDays ?? [],
    importedWorkoutIds: saved.importedWorkoutIds ?? [],
  };
}

export function exportState(state: AppState): string {
  return JSON.stringify(state, null, 2);
}

export function importState(json: string): AppState {
  const parsed = JSON.parse(json) as Partial<AppState>;
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('That file does not look like an Iron Ledger backup.');
  }
  return migrate(parsed);
}

export function downloadBackup(state: AppState): void {
  const blob = new Blob([exportState(state)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `iron-ledger-backup-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function clearState(): void {
  localStorage.removeItem(KEY);
}
